import type { ArticleInsight, PriceHistoryRow } from "../../api/articles";
import { toNumber } from "../../lib/format";

export type ArticlePriceKpis = {
  averagePrice: number | null;
  averageLineCount: number;
  pricedLineCount: number;
  bestPrice: number | null;
  bestRow: PriceHistoryRow | null;
  worstPrice: number | null;
  worstRow: PriceHistoryRow | null;
  spreadPct: number | null;
};

export function latestArticlePriceDate(
  article: ArticleInsight,
  rows: readonly PriceHistoryRow[],
): string | null {
  let latest = article.last_invoice_doc_date ?? article.last_order_doc_date;
  for (const row of rows) {
    if (row.doc_date && (!latest || row.doc_date > latest)) {
      latest = row.doc_date;
    }
  }
  return latest;
}

export function rollingMonthCutoffIso(
  anchorIso: string | null | undefined,
  months: number,
): string | null {
  if (!anchorIso || !Number.isInteger(months) || months < 0) return null;
  const iso = anchorIso.slice(0, 10);
  const [yearRaw, monthRaw, dayRaw] = iso.split("-", 3);
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > daysInMonth(year, month)
  ) {
    return null;
  }

  const targetMonthIndex = year * 12 + (month - 1) - months;
  const targetYear = Math.floor(targetMonthIndex / 12);
  const targetMonth = targetMonthIndex - targetYear * 12;
  if (targetYear < 1 || targetYear > 9999) return null;
  const targetDay = Math.min(day, daysInMonth(targetYear, targetMonth + 1));
  return [
    String(targetYear).padStart(4, "0"),
    String(targetMonth + 1).padStart(2, "0"),
    String(targetDay).padStart(2, "0"),
  ].join("-");
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    return leap ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

export function computeArticlePriceKpis(
  rows: readonly PriceHistoryRow[],
): ArticlePriceKpis {
  let sumWeightedPrice = 0;
  let sumQty = 0;
  let lineCount = 0;
  let pricedLineCount = 0;
  let bestPrice: number | null = null;
  let bestRow: PriceHistoryRow | null = null;
  let worstPrice: number | null = null;
  let worstRow: PriceHistoryRow | null = null;
  for (const row of rows) {
    const price = toNumber(row.net_unit_price);
    if (price === null || price <= 0) continue;
    pricedLineCount += 1;
    if (bestPrice === null || price < bestPrice) {
      bestPrice = price;
      bestRow = row;
    }
    if (worstPrice === null || price > worstPrice) {
      worstPrice = price;
      worstRow = row;
    }
    const qty = toNumber(row.qty);
    if (qty === null || qty <= 0) continue;
    sumWeightedPrice += price * qty;
    sumQty += qty;
    lineCount += 1;
  }

  const averagePrice = sumQty > 0 ? sumWeightedPrice / sumQty : null;
  return {
    averagePrice,
    averageLineCount: lineCount,
    pricedLineCount,
    bestPrice,
    bestRow,
    worstPrice,
    worstRow,
    spreadPct:
      bestPrice !== null && worstPrice !== null && bestPrice > 0
        ? ((worstPrice - bestPrice) / bestPrice) * 100
        : null,
  };
}
